/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

/**
 * Sanitizes a string to prevent XSS when rendered as text content.
 * Strips any HTML tags, event handlers, and javascript: URIs, then trims whitespace.
 */
function sanitizeText(str) {
  if (typeof str !== 'string') return '';
  return str
    .replace(/<[^>]*>/g, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .trim();
}

/**
 * Formats a numeric hourly cost into the display string: "$X.XX USD/Hour (Y.YY USD/Day)"
 */
function formatCostDisplay(hourlyRate) {
  const rate = parseFloat(hourlyRate);
  if (isNaN(rate)) return '';
  const daily = Math.round(rate * 24 * 100) / 100;
  return `${rate.toFixed(2)} USD/Hour (${daily.toFixed(2)} USD/Day)`;
}

/**
 * Resolves the estimated cost string for a given region from the estimatedCostInfo field.
 *
 * The field can be:
 *  - A plain string (legacy format, e.g. "0.05 USD/Hour (1.27 USD/Day)"): returned as-is after sanitization
 *  - A JSON string with region keys mapping to hourly rates:
 *    e.g. '{"ap-southeast-2":"0.52","us-east-1":"0.45"}'
 *    Returns a formatted cost string for the given region.
 *
 * @param {string} estimatedCostInfo - The raw estimatedCostInfo value from the config
 * @param {string} [region] - The target AWS region code (e.g. 'ap-southeast-2')
 * @returns {string} The resolved cost string, sanitized for safe display
 */
function getEstimatedCostForRegion(estimatedCostInfo, region) {
  if (!estimatedCostInfo) return '';

  // Attempt to parse as JSON (region-keyed object)
  if (typeof estimatedCostInfo === 'string' && estimatedCostInfo.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(estimatedCostInfo);

      // Validate it's a plain object with string values
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return sanitizeText(estimatedCostInfo);
      }

      // If a region is specified and exists in the map, format and return it
      if (region && Object.prototype.hasOwnProperty.call(parsed, region)) {
        return formatCostDisplay(parsed[region]);
      }

      // Fallback: try the primary region env var, then first available value
      const primaryRegion = typeof process !== 'undefined' && process.env && process.env.REACT_APP_AWS_REGION;
      if (primaryRegion && Object.prototype.hasOwnProperty.call(parsed, primaryRegion)) {
        return formatCostDisplay(parsed[primaryRegion]);
      }

      // Return first value as last resort
      const keys = Object.keys(parsed);
      if (keys.length > 0) {
        return formatCostDisplay(parsed[keys[0]]);
      }

      return '';
    } catch (e) {
      // Not valid JSON, fall through to plain text handling
    }
  }

  // Plain text fallback (legacy format)
  return sanitizeText(String(estimatedCostInfo));
}

export { getEstimatedCostForRegion, formatCostDisplay, sanitizeText };
